<script lang="ts" setup>
import { THEME, PROVIDE_THEME } from '@pkg/config/misc/theme';
import type { ProvideThemeInjection } from '@pkg/config/misc/theme';
import { provide, ref, watch } from 'vue';
import { cn } from '@pkg/ui/lib/utils';
import { useResolvedSectionTheme } from '@pkg/modular-blocks/frontend/useResolvedSectionTheme';

const props = defineProps<{
  theme: THEME,
  anchor?: string,
}>();

const resolvedTheme = useResolvedSectionTheme(() => props.theme);
const overridingTheme = ref<THEME | undefined>(resolvedTheme.value);
watch(resolvedTheme, (theme) => {
  overridingTheme.value = theme;
});

provide(PROVIDE_THEME, {
  theme: overridingTheme,
  setTheme: (newTheme: THEME) => {
    overridingTheme.value = newTheme;
  },
} satisfies ProvideThemeInjection);
</script>

<template>
  <section :id="anchor ? anchor : undefined" :class="cn('c-section', {'c-section--app': overridingTheme === THEME.APP})" :data-theme="overridingTheme">
    <slot />
  </section>
</template>

<style scoped>
@import '~/assets/css/mixins/_mixins.index.pcss';

/**
 * Two sections that follow each other in the DOM will have different top/bottom spacing based on their theme.
 * This Figma link explains how it works.
 * [0] To accomplish this, we set two variables, gap and gap-same-theme, to decide the overall spacing between the content in these sections.
 * [1] Then, if the sections have different themes, we split the gap in half and divide it equally between the two sections.
 * [2] However, if the sections are the same theme, we keep the bottom padding of the first section as-is (half), but remove extra padding-top from the second section.
 * [3] Lastly, for some blocks, even though they have the same theme, they don't want to be blended, so we override this here.
 * See: https://www.figma.com/file/gtNwvB7mZQMIGValvJ6sBF/TGTG---UI?type=design&node-id=1593-4336&mode=design&t=R6PuSzSFHKvWCvrK-4
 */

.c-section {
  --section-gap: 9rem; /* [0] */
  --section-gap-same-theme: 5rem; /* [0] */

  @screen tablet-wide {
    --section-gap: 12rem; /* [0] */
    --section-gap-same-theme: 7rem; /* [0] */
  }

  @screen desk {
    --section-gap: 15rem; /* [0] */
    --section-gap-same-theme: 9rem; /* [0] */

    &--app {
      --section-gap: 13rem; /* [0] */
      --section-gap-same-theme: 9rem; /* [0] */
    }
  }

  position: relative;
  z-index: theme('zIndex.page');
  overflow: hidden;

  background-color: var(--theme-background-color);
  color: var(--theme-text-color);

  --section-padding-top: calc(var(--section-gap) / 2); /* [1] */
  --section-padding-bottom: calc(var(--section-gap) / 2); /* [1] */

  padding-top: var(--section-padding-top);
  padding-bottom: var(--section-padding-bottom);
}

/* The CTA Section (c-cta-section) doesn't need reduced space based on theme, so is ignored. */
@mixin sibling-with-same-theme .c-section:not(.c-cta-section), .c-section {
  --section-padding-top: calc(var(--section-gap-same-theme) - (var(--section-gap) / 2)); /* [2] */
}

@mixin sibling-with-same-theme .c-main-hero, .c-section {
  /* The hero has an image or a background video, and so we don't want to make the next section blend if same theme, as we normally would */
  --section-padding-top: calc(var(--section-gap) / 2) !important; /* [3] */
}
</style>
